/**
 * Admin Dashboard component for multi-tenant admin dashboard and user management
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  Alert,
  Button,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Webhook as WebhookIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useAdmin } from '../../contexts/AdminContext';
import { formatDate, formatNumber } from '../../utils/formatters';

interface AdminDashboardProps {
  onNavigate?: (path: string) => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ onNavigate }) => {
  const {
    dashboardData,
    loading,
    error,
    loadDashboard,
    refreshData,
    clearError
  } = useAdmin();

  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshData();
    } finally {
      setRefreshing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <WarningIcon color="disabled" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading && !dashboardData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        action={
          <Button color="inherit" size="small" onClick={clearError}>
            Dismiss
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!dashboardData) {
    return (
      <Alert severity="info">
        No dashboard data available. Please try refreshing.
      </Alert>
    );
  }

  const { system_overview, user_metrics, system_health, recent_activity, alerts } = dashboardData;
  const healthStatus = system_health?.status ?? 'healthy';
  const health = {
    status: healthStatus,
    cpu_usage: system_health?.cpu_usage ?? 0,
    memory_usage: system_health?.memory_usage ?? 0,
    disk_usage: system_health?.disk_usage ?? 0,
    database_status: system_health?.database_status ?? 'healthy',
  };
  const overview = system_overview ?? {};
  const metrics = user_metrics ?? {};
  const subscriptionDist = metrics.subscription_distribution ?? {};
  const activityList = Array.isArray(recent_activity) ? recent_activity : [];
  const alertList = Array.isArray(alerts) ? alerts : [];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" display="flex" alignItems="center" gap={1}>
          <DashboardIcon />
          Admin Dashboard
        </Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh Data">
            <IconButton 
              onClick={handleRefresh} 
              disabled={refreshing}
              color="primary"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* System Health Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" display="flex" alignItems="center" gap={1}>
              <SecurityIcon />
              System Health
            </Typography>
            <Chip
              icon={getStatusIcon(healthStatus)}
              label={String(healthStatus).toUpperCase()}
              color={getStatusColor(healthStatus) as any}
              variant="outlined"
            />
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  CPU Usage
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <LinearProgress
                    variant="determinate"
                    value={health.cpu_usage}
                    sx={{ flexGrow: 1 }}
                  />
                  <Typography variant="body2">
                    {health.cpu_usage.toFixed(1)}%
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Memory Usage
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <LinearProgress
                    variant="determinate"
                    value={health.memory_usage}
                    sx={{ flexGrow: 1 }}
                  />
                  <Typography variant="body2">
                    {health.memory_usage.toFixed(1)}%
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Disk Usage
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <LinearProgress
                    variant="determinate"
                    value={system_health.disk_usage}
                    sx={{ flexGrow: 1 }}
                  />
                  <Typography variant="body2">
                    {system_health.disk_usage.toFixed(1)}%
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Database Status
                </Typography>
                <Chip
                  label={health.database_status}
                  color={health.database_status === 'healthy' ? 'success' : 'error'}
                  size="small"
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* System Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PeopleIcon color="primary" />
                <Typography variant="h6">
                  {formatNumber(overview.total_users ?? 0)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total Users
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatNumber(overview.active_users ?? 0)} active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <WebhookIcon color="primary" />
                <Typography variant="h6">
                  {formatNumber(overview.total_webhooks ?? 0)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total Webhooks
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatNumber(overview.active_webhooks ?? 0)} active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <NotificationsIcon color="primary" />
                <Typography variant="h6">
                  {formatNumber(overview.total_alerts_processed ?? 0)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Alerts Processed
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatNumber(overview.alerts_processed_today ?? 0)} today
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <SettingsIcon color="primary" />
                <Typography variant="h6">
                  {formatNumber(overview.total_subscriptions ?? 0)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Subscriptions
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatNumber(overview.active_subscriptions ?? 0)} active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* User Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>
                User Growth
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {formatNumber(user_metrics.new_users_today)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Today
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {formatNumber(metrics.new_users_this_week ?? 0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This Week
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {formatNumber(user_metrics.new_users_this_month)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This Month
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>
                Subscription Distribution
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(subscriptionDist).map(([tier, count]) => (
                  <Grid item xs={6} key={tier}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {tier}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {formatNumber(count)}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Activity and Alerts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>
                Recent Activity
              </Typography>
              <Box maxHeight="300px" overflow="auto">
                {activityList.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No recent activity
                  </Typography>
                ) : (
                  activityList.map((activity) => (
                    <Box key={activity.id} mb={2} p={1} border="1px solid" borderColor="divider" borderRadius={1}>
                      <Typography variant="body2" fontWeight="bold">
                        {String(activity.action ?? '').replace(/_/g, ' ').toUpperCase()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {activity.resource_type} • {formatDate(activity.timestamp)}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>
                System Alerts
              </Typography>
              <Box maxHeight="300px" overflow="auto">
                {alertList.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No active alerts
                  </Typography>
                ) : (
                  alertList.map((alert) => (
                    <Box key={alert.id} mb={2} p={1} border="1px solid" borderColor="divider" borderRadius={1}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Chip
                          label={String(alert.severity ?? 'info').toUpperCase()}
                          color={getPriorityColor(alert.severity ?? 'info') as any}
                          size="small"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(alert.timestamp)}
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        {alert.message}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
